// Configuración central de i18n.
//
// Estrategia:
// - Idiomas soportados: español (default) e inglés.
// - Detección: localStorage ("mb_lang") → idioma del navegador → fallback "es".
// - Namespaces por área (common, auth, dashboard, billing, …). Cada archivo
//   src/locales/<lang>/<namespace>.json se carga AUTOMÁTICAMENTE vía glob:
//   para agregar un área basta con crear su par es/<ns>.json + en/<ns>.json,
//   sin tocar este archivo. Así la migración por páginas no genera conflictos.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const SUPPORTED_LANGS = ["es", "en"];
export const DEFAULT_LANG = "es";
export const LANG_STORAGE_KEY = "mb_lang";

// Carga eager de todos los JSON de locales. Clave = ruta del módulo.
const modules = import.meta.glob("../locales/*/*.json", { eager: true });

// Construye { es: { common: {...}, ... }, en: { ... } } y junta la lista de namespaces.
const resources = {};
const namespaces = new Set();
for (const [path, mod] of Object.entries(modules)) {
  // path ej: "../locales/es/common.json"
  const match = path.match(/\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, lang, ns] = match;
  if (!resources[lang]) resources[lang] = {};
  resources[lang][ns] = mod.default ?? mod;
  namespaces.add(ns);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGS,
    // Normaliza "es-AR" → "es", "en-US" → "en".
    load: "languageOnly",
    ns: Array.from(namespaces),
    defaultNS: namespaces.has("common") ? "common" : Array.from(namespaces)[0],
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: {
      escapeValue: false, // React ya escapa
    },
    returnNull: false,
  });

// Mantener <html lang="…"> sincronizado con el idioma activo.
const applyHtmlLang = (lng) => {
  if (typeof document !== "undefined" && lng) {
    document.documentElement.setAttribute("lang", lng);
  }
};
applyHtmlLang(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", applyHtmlLang);

export default i18n;
